import React from 'react';
import { FadeIn } from '../ui/FadeIn';
import { CheckCircle2 } from 'lucide-react';
export function EmpathySection() {
  const targetAudience = ['Te sientes mentalmente saturada, con demasiadas cosas en la cabeza.', 'Te cuesta concentrarte o terminar lo que empiezas.', 'Estás cansada de exigirte y aun así sentir que «no alcanza».', 'Quieres volver a una rutina más ligera y con sentido.', 'Buscas algo espiritual pero práctico, sin complicarte la vida.'];
  return <section className="py-20 bg-white" data-id="element-58">
      <div className="max-w-3xl mx-auto px-6 lg:px-8" data-id="element-59">
        {/* Pain Point */}
        <div className="space-y-8 text-lg md:text-xl text-[#4A3F35] leading-relaxed mb-24" data-id="element-60">
          <FadeIn data-id="element-61">
            <p className="font-serif text-2xl md:text-3xl text-[#8B7355] text-center mb-12" data-id="element-62">
              ¿Te ha pasado que terminas el día cansada...
              <br data-id="element-63" />
              <span className="italic" data-id="element-64">pero tu mente sigue encendida?</span>
            </p>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-65">
            <p data-id="element-66">
              Que quieres enfocarte, avanzar, sentir paz... y aun así te
              distraes, postergas, te sobrecargas, y al final te quedas con esa
              sensación de:
            </p>
            <p className="text-center font-medium text-[#8B7355] my-8 text-xl" data-id="element-67">
              "Hoy tampoco pude conmigo".
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-68">
            <p data-id="element-69">Si estás en ese punto, quiero decirte algo con mucho respeto:</p>
            <p className="font-medium mt-4" data-id="element-70">No es que te falte disciplina.</p>
            <p className="font-serif text-2xl text-[#8B7355] mt-4" data-id="element-71">
              Muchas veces lo que falta es{' '}
              <span className="italic" data-id="element-72">centro</span>.
            </p>
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-73">
            <div className="bg-[#FAF7F2] p-8 rounded-2xl my-10 border border-[#E8E0D5]" data-id="element-74">
              <p className="mb-4" data-id="element-75">
                <strong data-id="element-76">Centro</strong> es ese lugar interno donde respiras y
                vuelves a ti.
              </p>
              <p data-id="element-77">
                Donde puedes pensar con claridad, elegir una cosa a la vez y
                cuidarte sin culpa.
              </p>
            </div>
          </FadeIn>
        </div>

        {/* Product Intro */}
        <div className="text-center mb-24" data-id="element-78">
          <FadeIn data-id="element-79">
            <p className="text-lg mb-4" data-id="element-80">Por eso creé este ebook:</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-8" data-id="element-81">
              Vuelve a tu Centro
            </h2>
            <p className="text-xl mb-6" data-id="element-82">
              Un plan de <strong data-id="element-83">14 días</strong>, con prácticas sencillas de{' '}
              <strong data-id="element-84">10 minutos al día</strong>, para ayudarte a recuperar:
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-8 my-10" data-id="element-85">
              <div className="bg-[#FAF7F2] px-6 py-4 rounded-xl text-[#8B7355] font-medium shadow-sm" data-id="element-86">
                Calma{' '}
                <span className="block text-sm font-normal text-[#6B5C4D] mt-1" data-id="element-87">
                  (en cuerpo y mente)
                </span>
              </div>
              <div className="bg-[#FAF7F2] px-6 py-4 rounded-xl text-[#8B7355] font-medium shadow-sm" data-id="element-88">
                Enfoque{' '}
                <span className="block text-sm font-normal text-[#6B5C4D] mt-1" data-id="element-89">
                  (sin agotarte)
                </span>
              </div>
              <div className="bg-[#FAF7F2] px-6 py-4 rounded-xl text-[#8B7355] font-medium shadow-sm" data-id="element-90">
                Bienestar{' '}
                <span className="block text-sm font-normal text-[#6B5C4D] mt-1" data-id="element-91">
                  (pasos reales y suaves)
                </span>
              </div>
            </div>

            <p className="text-xl italic text-[#6B5C4D]" data-id="element-92">
              No es un libro para leer y ya. Es una guía para{' '}
              <strong data-id="element-93">practicar</strong>.
            </p>
          </FadeIn>
        </div>

        {/* Who is this for */}
        <div className="bg-[#FAF7F2] rounded-3xl p-8 md:p-12 shadow-sm border border-[#E8E0D5]" data-id="element-94">
          <FadeIn data-id="element-95">
            <h3 className="text-3xl font-bold mb-8 text-center" data-id="element-96">
              ¿Para quién es este reinicio suave?
            </h3>
            <p className="text-lg mb-8 text-center" data-id="element-97">
              Este ebook es para ti si...
            </p>

            <ul className="space-y-6 mb-10" data-id="element-98">
              {targetAudience.map((item, index) => <li key={index} className="flex items-start gap-4" data-id="element-99">
                  <CheckCircle2 className="w-6 h-6 text-[#8B7355] flex-shrink-0 mt-1" data-id="element-100" />
                  <span className="text-lg text-[#4A3F35]" data-id="element-101">{item}</span>
                </li>)}
            </ul>

            <div className="pt-8 border-t border-[#E8E0D5]" data-id="element-102">
              <p className="text-lg text-center leading-relaxed" data-id="element-103">
                Y también es para ti si has intentado mil veces "organizarte" y
                no te ha funcionado, porque lo que realmente necesitabas no era
                hacer más...{' '}
                <strong className="text-[#8B7355]" data-id="element-104">sino regresar a ti</strong>.
              </p>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}